/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from '../Contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE_URL } from '../config/api';

import MarketingReport from './MarketingReport';
import ProjectsSidebar from '../components/ProjectsSidebar';
import SignInModal from '../components/SignInModal';
import { LightningBolt } from '../Shared/GradientTracing';
import AdvancedLoadingAnimation from '../Shared/AdvancedLoadingAnimation';
import StaticAdvancedAnimation from '../Shared/StaticAdvancedAnimation';
import SimpleAdvancedAnimation from '../Shared/SimpleAdvancedAnimation';
import AnimationErrorBoundary from '../Shared/AnimationErrorBoundary';

import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import particlesConfig from '../Shared/pr-s-black';

// Define character options
const characterOptions = [
  {
    label: 'Marketing Expert',
    description: 'Provides expert marketing strategies and insights',
  },
  {
    label: 'Sales Specialist',
    description: 'Focuses on sales-oriented marketing approaches',
  },
  {
    label: 'Brand Strategist',
    description: 'Specializes in brand development and positioning',
  },
  {
    label: 'Growth Hacker',
    description: 'Concentrates on rapid growth and optimization tactics',
  },
  {
    label: 'Social Media Manager',
    description: 'Expert in social media marketing strategies',
  },
];

// Define mode options
const modeOptions = [
  {
    label: 'Basic',
    description: 'Will generate a complete but simple report',
  },
  {
    label: 'Intermediate',
    description: 'Will generate a detailed report',
  },
  {
    label: 'Advanced',
    description: 'Will generate an in-depth report with detailed analytics',
  },
];

// Function to clean AI watermarks and formatting artifacts
const cleanAIWatermarks = (text) => {
  if (!text) return '';
  
  let cleaned = text;
  
  // Remove common AI watermarks and artifacts
  cleaned = cleaned.replace(/^--+$/gm, ''); // Remove lines with only dashes
  cleaned = cleaned.replace(/^—+$/gm, ''); // Remove lines with em-dashes
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n'); // Reduce multiple newlines to max 2
  cleaned = cleaned.replace(/^[\s-]*\*\*Note:\*\*.*$/gm, ''); // Remove "Note:" watermarks
  cleaned = cleaned.replace(/^[\s-]*\*\*Disclaimer:\*\*.*$/gm, ''); // Remove "Disclaimer:" watermarks
  cleaned = cleaned.replace(/^\s*---+\s*$/gm, ''); // Remove separator lines
  cleaned = cleaned.replace(/^\s*\*\*\*+\s*$/gm, ''); // Remove asterisk separators
  cleaned = cleaned.replace(/\[Assistant\]/gi, ''); // Remove [Assistant] tags
  cleaned = cleaned.replace(/\[AI\]/gi, ''); // Remove [AI] tags
  cleaned = cleaned.trim();
  
  return cleaned;
};

export default function MarketingGenius() {
  const { getAccessToken, isAuthenticated, user, loading } = useAuth();
  
  // Projects state
  const [currentProject, setCurrentProject] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Chat state
  const [aiInput, setAiInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Track selected output template
  const [isOutputFullscreen, setIsOutputFullscreen] = useState(false);
  const [isInputFullscreen, setIsInputFullscreen] = useState(false);
  
  // Settings state
  const [selectedCharacterIndex, setSelectedCharacterIndex] = useState('0');
  const [selectedModeIndex, setSelectedModeIndex] = useState('1'); // Default to Intermediate
  
  // Non-authenticated user prompt tracking
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [guestPromptCount, setGuestPromptCount] = useState(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem('mg_guest_prompt_count');
    return stored ? parseInt(stored, 10) : 0;
  });
  const [hasSeenSignInModal, setHasSeenSignInModal] = useState(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem('mg_has_seen_signin_modal');
    return stored === 'true';
  });
  
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  
  // Maximum prompts allowed for non-authenticated users (linked to Free tier)
  const MAX_GUEST_PROMPTS = 3;

  // Determine if we should show the title section
  // Show title if: not signed in, no project selected, OR project selected but no chat history
  const shouldShowTitle = !user || !currentProject || (currentProject && conversations.length === 0);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [conversations]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (isOutputFullscreen) {
          setIsOutputFullscreen(false);
        }
        if (isInputFullscreen) {
          setIsInputFullscreen(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isOutputFullscreen, isInputFullscreen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  const particlesLoaded = async (container) => {
    // Particles loaded callback
  };

  // Handle project selection
  const handleSelectProject = async (project) => {
    if (!project) {
      setCurrentProject(null);
      setConversations([]);
      return;
    }

    setCurrentProject(project);
    setIsLoading(true);

    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects/${project.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConversations(data.conversations || []);
          
          // Update character and mode from project
          const charIndex = characterOptions.findIndex(c => c.label === project.character_type);
          const modeIndex = modeOptions.findIndex(m => m.label === project.mode_type);
          
          if (charIndex >= 0) setSelectedCharacterIndex(String(charIndex));
          if (modeIndex >= 0) setSelectedModeIndex(String(modeIndex));
        }
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new project creation
  const handleNewProject = async (template) => {
    if (!isAuthenticated()) {
      alert('Please log in to create projects');
      return;
    }

    const title = prompt(`Enter a name for your ${template.name}:`, `New ${template.name}`);
    if (!title) return;

    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title,
          template_type: template.id,
          character_type: characterOptions[Number(selectedCharacterIndex)].label,
          mode_type: modeOptions[Number(selectedModeIndex)].label,
          description: template.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.project) {
          handleSelectProject(data.project);
        } else {
          alert('Failed to create project. Please try again.');
        }
      } else {
        alert('Failed to create project. Please try again.');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Error creating project. Please try again.');
    }
  };

  // Handle sending message
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (aiInput.trim() === '' || isLoading) return;

    // Check if user is not authenticated
    if (!user) {
      // Check if guest has exceeded prompt limit
      if (guestPromptCount >= MAX_GUEST_PROMPTS) {
        alert('You\'ve reached the maximum number of prompts for guests. Please sign in to continue.');
        return;
      }

      // Show modal after first prompt (only once)
      if (guestPromptCount === 0 && !hasSeenSignInModal) {
        // Set flag that modal has been seen
        setHasSeenSignInModal(true);
        localStorage.setItem('mg_has_seen_signin_modal', 'true');
      }
    }

    const userMessage = aiInput.trim();
    setAiInput('');
    setIsLoading(true);

    // Increment guest prompt count for non-authenticated users
    if (!user) {
      const newCount = guestPromptCount + 1;
      setGuestPromptCount(newCount);
      localStorage.setItem('mg_guest_prompt_count', newCount.toString());
      
      // Show modal after first prompt
      if (newCount === 1 && !hasSeenSignInModal) {
        setShowSignInModal(true);
      }
    }

    // Add user message to UI immediately
    const userMessageObj = {
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
      metadata: JSON.stringify({
        character_type: characterOptions[Number(selectedCharacterIndex)].label,
        mode_type: modeOptions[Number(selectedModeIndex)].label
      })
    };
    
    setConversations(prev => [...prev, userMessageObj]);

    try {
      let projectCreated = false;
      
      // If no project exists and user is authenticated, auto-create one based on the first message
      if (!currentProject && user) {
        const projectName = `Marketing Project - ${new Date().toLocaleDateString()}`;
        try {
          const token = getAccessToken();
          const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              title: projectName,
              template_type: selectedTemplate?.id || 'custom',
              character_type: characterOptions[Number(selectedCharacterIndex)].label,
              mode_type: modeOptions[Number(selectedModeIndex)].label,
              description: `Auto-created project based on: "${userMessage}"`,
              output_template: selectedTemplate
            })
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Project creation response:', data);
            if (data.success && data.project) {
              setCurrentProject(data.project);
              projectCreated = true;
              console.log('Project created successfully:', data.project);
              // Refresh the projects list in the sidebar
              // The ProjectsSidebar component will automatically refresh
            } else {
              console.error('Project creation failed:', data);
            }
          } else {
            const errorData = await response.json();
            console.error('Project creation HTTP error:', response.status, errorData);
          }
        } catch (error) {
          console.error('Error creating auto-project:', error);
          projectCreated = false;
        }
      } else if (currentProject) {
        projectCreated = true;
      }

      // If we have a project (or just created one), use the project-specific endpoint
      if (projectCreated && currentProject) {
        const token = getAccessToken();
        const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects/${currentProject.id}/chat`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: userMessage,
            character_type: characterOptions[Number(selectedCharacterIndex)].label,
            mode_type: modeOptions[Number(selectedModeIndex)].label,
            output_template: selectedTemplate
          })
        });

        if (response.ok) {
          // Handle streaming response
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let aiResponse = '';

          // Create AI message placeholder
          const aiMessageObj = {
            role: 'assistant',
            content: '',
            created_at: new Date().toISOString(),
            metadata: JSON.stringify({
              character_type: characterOptions[Number(selectedCharacterIndex)].label,
              mode_type: modeOptions[Number(selectedModeIndex)].label
            }),
            streaming: true
          };
          
          setConversations(prev => [...prev, aiMessageObj]);

          // Read the stream
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            aiResponse += chunk;

            // Update the streaming message
            setConversations(prev => {
              const newConvs = [...prev];
              const lastIndex = newConvs.length - 1;
              newConvs[lastIndex] = {
                ...newConvs[lastIndex],
                content: aiResponse
              };
              return newConvs;
            });
          }

          // Mark streaming as complete
          setConversations(prev => {
            const newConvs = [...prev];
            const lastIndex = newConvs.length - 1;
            if (newConvs[lastIndex]) {
              delete newConvs[lastIndex].streaming;
            }
            return newConvs;
          });

        } else {
          throw new Error('Failed to get response');
        }
      } else if (!projectCreated) {
        // Only show fallback if project creation failed
        const aiResponse = `As a ${characterOptions[Number(selectedCharacterIndex)].label} in ${modeOptions[Number(selectedModeIndex)].label} mode, I'd be happy to help with your marketing question: "${userMessage}". 

To get the most personalized and detailed assistance, I recommend creating a project first using one of the templates above. This will allow me to maintain context throughout our conversation and provide more targeted advice.

Would you like to create a project to continue our discussion?`;
        
        setConversations(prev => [...prev, {
          role: 'assistant',
          content: aiResponse,
          created_at: new Date().toISOString(),
          metadata: JSON.stringify({
            character_type: characterOptions[Number(selectedCharacterIndex)].label,
            mode_type: modeOptions[Number(selectedModeIndex)].label
          })
        }]);
      } else {
        // If no project, use real AI with a simple prompt (works for both authenticated and guest users)
        try {
          const token = getAccessToken();
          const headers = {
            'Content-Type': 'application/json'
          };
          
          // Only add auth header if user is authenticated
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${API_BASE_URL}/api/marketing-genius/chat`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
              message: userMessage,
              character_type: characterOptions[Number(selectedCharacterIndex)].label,
              mode_type: modeOptions[Number(selectedModeIndex)].label,
              is_guest: !user // Flag to indicate guest user
            })
          });

          if (response.ok) {
            // Handle streaming response
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let aiResponse = '';

            // Create AI message placeholder
            const aiMessageObj = {
              role: 'assistant',
              content: '',
              created_at: new Date().toISOString(),
              metadata: JSON.stringify({
                character_type: characterOptions[Number(selectedCharacterIndex)].label,
                mode_type: modeOptions[Number(selectedModeIndex)].label
              }),
              streaming: true
            };
            
            setConversations(prev => [...prev, aiMessageObj]);

            // Read the stream
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value);
              aiResponse += chunk;

              // Update the streaming message
              setConversations(prev => {
                const newConvs = [...prev];
                const lastIndex = newConvs.length - 1;
                newConvs[lastIndex] = {
                  ...newConvs[lastIndex],
                  content: aiResponse
                };
                return newConvs;
              });
            }

            // Mark streaming as complete
            setConversations(prev => {
              const newConvs = [...prev];
              const lastIndex = newConvs.length - 1;
              if (newConvs[lastIndex]) {
                delete newConvs[lastIndex].streaming;
              }
              return newConvs;
            });

          } else {
            throw new Error('Failed to get AI response');
          }
        } catch (error) {
          console.error('Error getting AI response:', error);
          // Fallback to simple response
          const aiResponse = `As a ${characterOptions[Number(selectedCharacterIndex)].label} in ${modeOptions[Number(selectedModeIndex)].label} mode, I'd be happy to help with your marketing question: "${userMessage}". 

To get the most personalized and detailed assistance, I recommend creating a project first using one of the templates above. This will allow me to maintain context throughout our conversation and provide more targeted advice.

Would you like to create a project to continue our discussion?`;
          
          setConversations(prev => [...prev, {
            role: 'assistant',
            content: aiResponse,
            created_at: new Date().toISOString(),
            metadata: JSON.stringify({
              character_type: characterOptions[Number(selectedCharacterIndex)].label,
              mode_type: modeOptions[Number(selectedModeIndex)].label
            })
          }]);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setConversations(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString(),
        metadata: '{}'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAiInputChange = (event) => {
    setAiInput(event.target.value);
  };

  const openUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      console.log("File selected:", file.name);
    }
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current.click();
  };

  const handleUpload = () => {
    if (selectedFile) {
      console.log("Uploading file:", selectedFile.name);
      const fileReference = `[Attached file: ${selectedFile.name}]`;
      setAiInput(prevInput => {
        return prevInput.trim() 
          ? `${prevInput.trim()}\n\n${fileReference}` 
          : fileReference;
      });
      closeUploadModal();
    }
  };

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isUploadModalOpen) {
        closeUploadModal();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isUploadModalOpen]);

  const handleKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  // Handle modal actions
  const handleContinueWithoutSignIn = () => {
    // User chose to continue without signing in
    // The prompt count is already tracked, just close the modal
    setShowSignInModal(false);
  };

  const handleCloseModal = () => {
    setShowSignInModal(false);
  };

  return (
    <div id="marketing-genius-page">
      {/* Projects Sidebar */}
      {!isOutputFullscreen && !isInputFullscreen && (
        <ProjectsSidebar
          currentProjectId={currentProject?.id}
          onSelectProject={handleSelectProject}
          onNewProject={handleNewProject}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />
      )}

      {/* Main Content */}
      <div className={`marketing-genius-content ${sidebarOpen ? 'sidebar-open' : ''} ${!shouldShowTitle ? 'no-header-spacing' : ''}`}>
      {shouldShowTitle && (
      <section className="marketing-genius-header">
        <div className="particles circle-bg">
          <div className="container">
            <div className="title-box">
              <h1>
                <span className="color-font">MARKETING GENIUS</span>
              </h1>
            </div>
          </div>
          
      <Particles id="particles-js" init={particlesInit} loaded={particlesLoaded} options={particlesConfig} />
          
          <div className="gradient-circle"></div>
          <div className="gradient-circle two"></div>
        </div>
      </section>
      )}
      
      <section className="services">
        <div className="container">
          {shouldShowTitle && (
          <div className="marketing-services-intro">
            <p>
              Our Marketing Genius uses advanced AI to analyze and optimize your marketing strategy.
              <span className="highlight"> Businesses using AI-powered marketing insights see up to 40% increase in engagement</span>,
              leading to higher conversion rates and improved ROI.
            </p>
        </div>
          )}

            {currentProject && (
              <h2><span className="color-font">{currentProject.title}</span></h2>
            )}
            
            {/* Conversation History - Always Visible */}
            {conversations.length > 0 && !isInputFullscreen && (
              <div 
                className={`conversation-container ${isOutputFullscreen ? 'fullscreen' : ''}`}
                onClick={(e) => {
                  if (isOutputFullscreen && e.target.classList.contains('conversation-container')) {
                    setIsOutputFullscreen(false);
                  }
                }}
              >
                <div className="conversation-header">
                  {isOutputFullscreen ? (
                    <button 
                      className="fullscreen-btn"
                      type="button"
                      onClick={() => {
                        setIsOutputFullscreen(false);
                      }}
                      title="Exit Fullscreen (ESC)"
                    >
                      ✕
                    </button>
                  ) : (
                    <button 
                      className="fullscreen-btn"
                      type="button"
                      onClick={() => {
                        setIsOutputFullscreen(true);
                      }}
                      title="Enter Fullscreen"
                    >
                      ⤢
                    </button>
                  )}
                </div>
                <div className="conversation-history">
                  {conversations.filter(conv => conv.content && conv.content.trim()).map((conv, index) => (
                    <div key={index} className={`message ${conv.role}`}>
                      <div className="message-avatar">
                        {conv.role === 'user' ? '👤' : '🤖'}
                      </div>
                      <div className="message-content">
                        {conv.role !== 'system' && (
                          <div className="message-header">
                            <span className="message-role">
                              {conv.role === 'user' ? 'You' : 'Marketing Genius'}
                            </span>
                            {conv.metadata && (() => {
                              try {
                                const meta = JSON.parse(conv.metadata);
                                return (
                                  <span className="message-meta">
                                    {meta.character_type && (
                                      <span className="meta-tag character">{meta.character_type}</span>
                                    )}
                                    {meta.mode_type && (
                                      <span className="meta-tag mode">{meta.mode_type}</span>
                                    )}
                                  </span>
                                );
                              } catch (e) {
                                return null;
                              }
                            })()}
                          </div>
                        )}
                        <div className="message-text">
                          {conv.role === 'user' ? (
                            // User messages: plain text
                            <div>{conv.content}</div>
                          ) : (
                            // AI messages: rich text with markdown
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]}
                              components={{
                                // Custom rendering for better styling
                                h1: ({node, ...props}) => <h1 style={{marginTop: '1rem', marginBottom: '0.5rem'}} {...props} />,
                                h2: ({node, ...props}) => <h2 style={{marginTop: '0.9rem', marginBottom: '0.4rem'}} {...props} />,
                                h3: ({node, ...props}) => <h3 style={{marginTop: '0.8rem', marginBottom: '0.3rem'}} {...props} />,
                                p: ({node, ...props}) => <p style={{marginBottom: '0.8rem', lineHeight: '1.6'}} {...props} />,
                                ul: ({node, ...props}) => <ul style={{marginLeft: '1.5rem', marginBottom: '0.8rem'}} {...props} />,
                                ol: ({node, ...props}) => <ol style={{marginLeft: '1.5rem', marginBottom: '0.8rem'}} {...props} />,
                                li: ({node, ...props}) => <li style={{marginBottom: '0.3rem'}} {...props} />,
                                blockquote: ({node, ...props}) => <blockquote style={{borderLeft: '3px solid #ccc', paddingLeft: '1rem', marginLeft: 0, fontStyle: 'italic'}} {...props} />,
                                code: ({node, inline, ...props}) => inline ? 
                                  <code style={{backgroundColor: '#f4f4f4', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace'}} {...props} /> : 
                                  <code style={{display: 'block', backgroundColor: '#f4f4f4', padding: '1rem', borderRadius: '5px', overflowX: 'auto', fontFamily: 'monospace'}} {...props} />,
                                table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', width: '100%', marginBottom: '1rem'}} {...props} />,
                                th: ({node, ...props}) => <th style={{border: '1px solid #ddd', padding: '8px', backgroundColor: '#f2f2f2'}} {...props} />,
                                td: ({node, ...props}) => <td style={{border: '1px solid #ddd', padding: '8px'}} {...props} />,
                              }}
                            >
                              {cleanAIWatermarks(conv.content)}
                            </ReactMarkdown>
                          )}
                          {conv.streaming && <span className="streaming-cursor">▊</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}
            
            {/* Chat Interface - Always Visible (unless output is fullscreen) */}
            {!isOutputFullscreen && (
              <div 
                className={`chat-interface ${isInputFullscreen ? 'fullscreen' : ''}`}
                onClick={(e) => {
                  if (isInputFullscreen && e.target.classList.contains('chat-interface')) {
                    setIsInputFullscreen(false);
                  }
                }}
              >
              <div className="chat-header">
                {isInputFullscreen ? (
                  <button 
                    className="fullscreen-btn"
                    type="button"
                    onClick={() => {
                      setIsInputFullscreen(false);
                    }}
                    title="Exit Fullscreen (ESC)"
                  >
                    ✕
                  </button>
                ) : (
                  <button 
                    className="fullscreen-btn"
                    type="button"
                    onClick={() => {
                      setIsInputFullscreen(true);
                    }}
                    title="Enter Fullscreen"
                  >
                    ⤢
                  </button>
                )}
              </div>
            <form onSubmit={handleSubmit}>
              <div className="chat-input-container">
                <textarea 
                    ref={chatInputRef}
                  id="mg-ai-input" 
                  placeholder="Describe your marketing needs or ask a question..." 
                  value={aiInput} 
                  onChange={handleAiInputChange}
                  onKeyDown={handleKeyDown}
                  className="chat-input"
                    disabled={isLoading}
                />
                <div className="input-controls">
                  <button 
                    type="button" 
                    className="attachment-button"
                    onClick={openUploadModal}
                    title="Attach a file"
                      disabled={isLoading}
                  >
                    <span style={{ fontSize: '1.2rem' }}>📎</span>
                  </button>
                  
                  <div className="chat-option-buttons">
                    <div className="simple-dropdown">
                      <select 
                        className="dropdown-select"
                        value={selectedCharacterIndex}
                        onChange={(e) => setSelectedCharacterIndex(e.target.value)}
                          disabled={isLoading}
                      >
                        {characterOptions.map((option, index) => (
                          <option key={index} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="simple-dropdown">
                      <select 
                        className="dropdown-select"
                        value={selectedModeIndex} 
                        onChange={(e) => setSelectedModeIndex(e.target.value)}
                          disabled={isLoading}
                      >
                        {modeOptions.map((option, index) => (
                          <option key={index} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                    <button 
                      type="submit" 
                      className="chat-submit-button"
                      disabled={isLoading || !aiInput.trim()}
                    >
                      <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                        {isLoading ? '⏳' : '➤'}
                      </span>
                  </button>
                </div>
              </div>
            
                {/* Template Selection Inside Chat Interface */}
                <div className="chat-templates">
                  <button 
                    type="button"
                    className="see-templates-btn"
                    onClick={() => setShowTemplates(!showTemplates)}
                  >
                    {showTemplates ? 'Hide Templates' : 'See Templates'}
                  </button>
                  
                  {showTemplates && (
                    <div className="template-buttons">
                      <button 
                        onClick={() => setSelectedTemplate({id: 'marketing-plan', name: 'Marketing Plan', description: 'Comprehensive marketing strategy development'})} 
                        className={`template-btn ${selectedTemplate?.id === 'marketing-plan' ? 'selected' : ''}`}
                      >
                        📊 Marketing Plan
                      </button>
                      <button 
                        onClick={() => setSelectedTemplate({id: 'social-media', name: 'Social Media Analysis', description: 'Social media strategy and content planning'})} 
                        className={`template-btn ${selectedTemplate?.id === 'social-media' ? 'selected' : ''}`}
                      >
                        📱 Social Media
                      </button>
                      <button 
                        onClick={() => setSelectedTemplate({id: 'multimedia', name: 'Multimedia Analysis', description: 'Video, audio, and visual content strategy'})} 
                        className={`template-btn ${selectedTemplate?.id === 'multimedia' ? 'selected' : ''}`}
                      >
                        🎬 Multimedia
                      </button>
                      <button 
                        onClick={() => setSelectedTemplate({id: 'seo-audit', name: 'SEO Audit', description: 'Search engine optimization analysis'})} 
                        className={`template-btn ${selectedTemplate?.id === 'seo-audit' ? 'selected' : ''}`}
                      >
                        🔍 SEO Audit
                      </button>
                      <button 
                        onClick={() => setSelectedTemplate({id: 'website-audit', name: 'Website Audit', description: 'Website performance and optimization'})} 
                        className={`template-btn ${selectedTemplate?.id === 'website-audit' ? 'selected' : ''}`}
                      >
                        🌐 Website Audit
                      </button>
                      <button 
                        onClick={() => setSelectedTemplate({id: 'product-launch', name: 'Product Launch Campaign', description: 'End-to-end product launch strategy'})} 
                        className={`template-btn ${selectedTemplate?.id === 'product-launch' ? 'selected' : ''}`}
                      >
                        🚀 Product Launch
                      </button>
              <button 
                        onClick={() => setSelectedTemplate({id: 'brand-refresh', name: 'Brand Refresh Strategy', description: 'Brand identity and positioning overhaul'})} 
                        className={`template-btn ${selectedTemplate?.id === 'brand-refresh' ? 'selected' : ''}`}
              >
                        🎨 Brand Refresh
              </button>
              <button 
                        onClick={() => setSelectedTemplate({id: 'competitive-response', name: 'Competitive Response Plan', description: 'Counter-competitive strategies'})} 
                        className={`template-btn ${selectedTemplate?.id === 'competitive-response' ? 'selected' : ''}`}
              >
                        ⚔️ Competitive Response
              </button>
              <button 
                        onClick={() => setSelectedTemplate({id: 'content-strategy', name: 'SEO & Content Strategy', description: 'Content marketing and SEO optimization'})} 
                        className={`template-btn ${selectedTemplate?.id === 'content-strategy' ? 'selected' : ''}`}
              >
                        📝 Content Strategy
              </button>
              <button 
                        onClick={() => setSelectedTemplate({id: 'social-takeover', name: 'Social Media Takeover', description: 'Aggressive social media growth strategy'})} 
                        className={`template-btn ${selectedTemplate?.id === 'social-takeover' ? 'selected' : ''}`}
              >
                        📈 Social Takeover
              </button>
              <button 
                        onClick={() => setSelectedTemplate(null)} 
                        className={`template-btn ${!selectedTemplate ? 'selected' : ''}`}
              >
                        ✨ Custom Output
              </button>
            </div>
                  )}
          </div>
              </form>
            </div>
          )}
        </div>
      </section>
      
      {/* File Upload Modal */}
      {isUploadModalOpen && (
        <div className="upload-modal-overlay" onClick={closeUploadModal}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <span 
              className="close-icon" 
              onClick={closeUploadModal}
              aria-label="Close upload modal"
            >
              ✕
            </span>
            <h3>Upload File</h3>
            <p className="modal-description">
              Upload a file to include with your query. Supports PDF, DOCX, TXT, CSV, and images.
            </p>
            <div className="upload-area" onClick={handleUploadAreaClick}>
              <span className="upload-icon">⬆️</span>
              <p>
                {selectedFile ? 
                  selectedFile.name : 
                  'Drag & drop a file here or click to browse'
                }
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.txt,.csv,.jpg,.jpeg,.png"
              />
            </div>
            <button 
              className="upload-button"
              onClick={handleUpload}
              disabled={!selectedFile}
            >
              <span className="button-icon">⬆️</span>
              Upload File
            </button>
          </div>
        </div>
      )}

      {/* Sign In Modal for Non-Authenticated Users */}
      <SignInModal
        isOpen={showSignInModal}
        onClose={handleCloseModal}
        onContinueWithoutSignIn={handleContinueWithoutSignIn}
      />
      </div>
    </div>
  );
}
